import { Injectable } from '@angular/core'
import { Params, Router } from '@angular/router'
import { BehaviorSubject } from 'rxjs'
import { jwtDecode } from 'jwt-decode';
import { SessionData, UserLogged, UserResponsePayload, emptyUser, AccessResponse } from 'src/app/core/models/auth/session-data.model'
import { ClaveunicaLoginService } from '../claveunica/clave-unica-login.service'
import moment from 'moment'
import { environment } from '../../../../../environments/environments'
import { RedirectReasonCode } from 'src/app/core/models/others/redirect-reason.model';

@Injectable({
  providedIn: 'root',
})
export class SessionManager {
  private sessionSubject = new BehaviorSubject<SessionData>(emptyUser)
  session$ = this.sessionSubject.asObservable()
  sessionData!: SessionData
  userLogged: UserLogged | undefined

  timeReviewSession: number = 60 // segundos
  accessCode: string = ''
  accessState: string = ''
  instance: boolean = false

  queryParams: Params = {
    redirect_reason: null
  }

  constructor(private router: Router, private claveunicaService: ClaveunicaLoginService) {
    this.watcher()
    if (!this.instance) {
      this.instance = true
      setInterval(() => {
        this.watcher()
      }, this.timeReviewSession * 1000)
    }
  }

  watcher() {
    this.sessionData = this.getSessionStorage()
    const localSession = this.getSessionStorage()
    this.sessionSubject.next(this.sessionData)

    localSession.logged ? this.validSession(this.sessionData) : this.invalidSession()
  }

  async validSession(sessionData: SessionData) {
    this.getAllowQueries();
    // Si la sesión esta iniciada, no puede estar en vistas de login
    if (this.validateIsPathname(['/auth/callback', '/auth/login'])) {
      this.router.navigate(['/'])
    }

    if (this.validateIsPathname(['/auth/logout'])) {
      this.clearSessionState();
      this.router.navigate(['/auth/login'], { queryParams: this.queryParams });
    }

  }

  unauthorizedLogout(error: any) {
    if (error.status === 401 || error.status === 0) {
      this.router.navigate(['/auth/login'])
      this.setSessionState(emptyUser)
    }
  }

  invalidSession() {
    this.getAccessCode();
    this.getAllowQueries();

    // Sin sesión y en una ruta invalida, redirige al LOGIN
    if (!this.validateIsPathname(['/auth/callback', '/auth/logout', '/auth/login', '/auth/error'])) {
      this.router.navigate(['/auth/login'], { queryParams: this.queryParams })
      this.clearSessionState();
    }

    // El access code o state code no son validos, redirige al LOGIN
    if (this.validateIsPathname(['/auth/callback']) && (this.accessCode === '' || this.accessState === '')) {
      this.router.navigate(['/auth/login'], { queryParams: this.queryParams }) // PENDIENTE: AQUI DEBE ENVIAR A /AUTH/ERROR
      this.clearSessionState();
      throw 'ERROR: CODE o STATE no se han procesado correctamente'
    }

    // Si hay un access code y state code inicia el proceso de login
    if (this.validateIsPathname(['/auth/callback']) && this.accessCode !== '' && this.accessState !== '') {
      this.getAccessToken();
    }

    if (this.validateIsPathname(['/auth/logout', '/auth/error'])) {
      this.clearSessionState();
      this.router.navigate(['/auth/login'], { queryParams: this.queryParams });
      this.clearSessionState();
    }

  }

  setSessionStateFromToken(userResponse: AccessResponse) {

    const localSession = this.getSessionStorage();
    sessionStorage.setItem('token', userResponse.accessToken || '');

    const payload = jwtDecode<UserResponsePayload>(userResponse.accessToken);
    const { examiner_municipalities } = payload;

    if (!localSession.logged) {
      this.userLogged = {
        token: userResponse.accessToken,
        run: payload.run,
        name: payload.name,
        last_name: payload.last_name,
        photo: "/9j/4AAQSk",
        municipalities: examiner_municipalities,
        time: moment(),
      };

      if (examiner_municipalities.length === 1) {
        const firstMunicipality = examiner_municipalities[0];
        this.userLogged.id_municipality = firstMunicipality.id_municipality;
        this.userLogged.name_municipality = firstMunicipality.name_municipality;
        this.userLogged.role = firstMunicipality.role;
        this.userLogged.role_permissions = firstMunicipality.role_permissions;
      }

      this.sessionData.logged = true;
    } else {
      this.userLogged = { ...localSession.data, time: moment() };
    }

    this.sessionData.data = this.userLogged;

    this.setSessionState(this.sessionData);

    this.sessionSubject.next(this.sessionData);

  }


  setSessionState(state: SessionData): void {
    sessionStorage.setItem('session', JSON.stringify(state))
  }

  async getAccessToken() {
    ; (await this.claveunicaService.login(this.accessCode, this.accessState)).subscribe({
      next: (response: AccessResponse) => {
        const userResponse = response

        if (!userResponse.accessToken || !userResponse.refreshToken) {
          throw new Error('No se obtuvieron los tokens de autorizacion correctamnte');
        }

        sessionStorage.setItem('refresh', userResponse.refreshToken);
        this.setSessionStateFromToken(userResponse);

        if (this.userLogged?.id_municipality != null) {
          this.router.navigate(['/applicant'])
        } else {
          this.router.navigate(['/auth/municipality-selection'])
        }
      },
      error: (error: any) => {
        console.error('Error while getting access token:', error)
        this.router.navigate(['/auth/login'])
      },
    })
  }

  getAccessCode(): void {
    const params = new URLSearchParams(window.location.search)
    this.accessCode = params.getAll('code')[0] || '';
    this.accessState = params.getAll('state')[0] || '';
  }

  getAllowQueries(): void {
    const params = new URLSearchParams(window.location.search)
    this.queryParams = {
      redirect_reason: params.getAll('redirect_reason')[0] || null
    }
  }

  validateIsPathname(paths: string[]): boolean {
    const pathname = window.location.pathname
    return paths.some((path: string) => path == pathname)
  }

  static loginWithSql(user: object) {
    const state = {
      logged: true,
      data: { ...user },
    }
    window.sessionStorage.setItem('session', JSON.stringify(state))
  }

  redirectLogout(redirectReason?: RedirectReasonCode) {
    window.location.href = "https://accounts.claveunica.gob.cl/api/v1/accounts/app/logout";

    let loginRedirect = environment.claveunica.claveunica_logout_uri;
    if (redirectReason) {
      const url = new URL(loginRedirect);
      url.searchParams.set('redirect_reason', redirectReason);
      loginRedirect = url.toString();
    }

    setTimeout(function () {
      window.location.href = loginRedirect;
    }, 1000);
  }

  claveUnicaLogout(redirectReason?: RedirectReasonCode) {
    const refreshToken = sessionStorage.getItem('refresh') || '';
    this.claveunicaService.logout(refreshToken, redirectReason).subscribe({
      next: () => {
        this.redirectLogout(redirectReason);
      },
      error: (error) => {
        console.error(error);
        this.redirectLogout(redirectReason);
      }
    })

  }

  claveUnicaRefresh() {
    const refreshToken = sessionStorage.getItem('refresh') || '';
    return this.claveunicaService.refresh(refreshToken);
  }

  getSessionStorage(): SessionData {
    const sessionData = window.sessionStorage.getItem('session')
    return typeof sessionData === 'string' ? JSON.parse(sessionData) : emptyUser
  }

  clearSessionState() {
    sessionStorage.setItem('session', JSON.stringify(emptyUser))
    sessionStorage.removeItem('token')
    sessionStorage.removeItem('refresh')

  }
}
